import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type {
  ImportAction,
  ImportFighter,
  ImportFighterMatch,
  ImportResult,
} from "@mma/shared";
import { api, errorMessage } from "../api.js";

interface EventOption {
  id: string;
  name: string;
  date: string;
}

const dk = (n: number) => String(n).replace(".", ",");

// "Vol.35" → "MMA Galla Vol 35" (navneskikken for de eksisterende events)
function suggestName(sheet: string): string {
  const m = sheet.match(/^vol\.?\s*(\d+)$/i);
  return m ? `MMA Galla Vol ${m[1]}` : sheet;
}

export function AdminImportPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventOption[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [sheet, setSheet] = useState<string | null>(null);
  const [targetId, setTargetId] = useState<string | null>(params.get("eventId"));
  const [newEvent, setNewEvent] = useState({ name: "", date: "", time: "", location: "" });
  const [suggested, setSuggested] = useState("");
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<EventOption[]>("/api/events").then(setEvents);
  }, []);

  function query(apply: boolean) {
    const qs = new URLSearchParams();
    if (sheet) qs.set("sheet", sheet);
    if (targetId) qs.set("eventId", targetId);
    if (apply) {
      qs.set("apply", "1");
      if (!targetId) {
        qs.set("name", newEvent.name.trim());
        qs.set("date", new Date(`${newEvent.date}T${newEvent.time || "18:00"}`).toISOString());
        if (newEvent.location.trim()) qs.set("location", newEvent.location.trim());
      }
    }
    return `/api/import/sheet?${qs}`;
  }

  // Preview hver gang fil, fane eller mål-event skifter.
  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    setBusy(true);
    setError(null);
    api
      .upload<ImportResult>(query(false), file)
      .then((r) => {
        if (cancelled) return;
        setResult(r);
        const name = suggestName(r.sheet);
        setNewEvent((s) => ({
          ...s,
          // Overskriv kun navnet hvis det stadig er vores eget forslag.
          name: !s.name || s.name === suggested ? name : s.name,
          time: s.time || r.liveTime || r.firstStartTime || "",
        }));
        setSuggested(name);
      })
      .catch((e) => {
        if (cancelled) return;
        setResult(null);
        setError(errorMessage(e, "Filen kunne ikke læses"));
      })
      .finally(() => !cancelled && setBusy(false));
    return () => {
      cancelled = true;
    };
  }, [file, sheet, targetId]);

  async function apply() {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.upload<ImportResult>(query(true), file);
      setResult(r);
      if (r.eventId) navigate(`/admin/events/${r.eventId}`);
    } catch (e) {
      setError(errorMessage(e, "Importen fejlede — intet er gemt"));
    } finally {
      setBusy(false);
    }
  }

  const target = events.find((e) => e.id === targetId);
  const duplicate =
    !targetId &&
    events.find((e) => e.name.trim().toLowerCase() === newEvent.name.trim().toLowerCase());
  const creates = result?.actions.filter((a) => a.kind === "create").length ?? 0;
  const updates = result?.actions.filter((a) => a.kind === "update").length ?? 0;
  const unchanged = result?.actions.filter((a) => a.kind === "unchanged").length ?? 0;
  const nothingToDo = !!result && creates + updates === 0;
  const canApply =
    !!result && !busy && !nothingToDo && (!!targetId || (!!newEvent.name.trim() && !!newEvent.date));

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Importér fight card fra ark</h1>
        <Link
          to={targetId ? `/admin/events/${targetId}` : "/admin"}
          className="text-sm text-neutral-400 hover:text-neutral-200"
        >
          ← Tilbage
        </Link>
      </header>

      <p className="text-sm text-neutral-400">
        I Google Sheets: <em>Filer → Download → Microsoft Excel (.xlsx)</em>. Upload filen
        her og vælg fanen for gallaen. Intet gemmes før du trykker Importér. Genimport
        opdaterer eksisterende kampe — stats og resultater røres aldrig.
      </p>

      <div className="bg-neutral-900 p-4 rounded-xl border border-neutral-800 grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1 text-xs text-neutral-400">
          Fil (.xlsx)
          <input
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => {
              setSheet(null);
              setFile(e.target.files?.[0] ?? null);
            }}
            className="text-sm text-neutral-200"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-neutral-400">
          Fane
          <select
            // Uden valgt fane bruger serveren den første; vis hvilken det blev.
            value={sheet ?? result?.sheet ?? ""}
            disabled={!result}
            onChange={(e) => setSheet(e.target.value)}
            className="bg-neutral-800 rounded px-3 py-2 text-base text-neutral-100 disabled:opacity-40"
          >
            {(result?.sheets ?? []).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-neutral-400 col-span-2">
          Importér til
          <select
            value={targetId ?? ""}
            onChange={(e) => setTargetId(e.target.value || null)}
            className="bg-neutral-800 rounded px-3 py-2 text-base text-neutral-100"
          >
            <option value="">Nyt event</option>
            {events.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name} ({new Date(e.date).toLocaleDateString("da-DK")})
              </option>
            ))}
          </select>
        </label>

        {!targetId && (
          <>
            <label className="flex flex-col gap-1 text-xs text-neutral-400 col-span-2">
              Navn på nyt event
              <input
                value={newEvent.name}
                onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                className="bg-neutral-800 rounded px-3 py-2 text-base text-neutral-100"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-neutral-400">
              Dato (styrer også deploy-frysning)
              <input
                type="date"
                value={newEvent.date}
                onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                className="bg-neutral-800 rounded px-3 py-2 text-base text-neutral-100"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-neutral-400">
              Tid
              <input
                type="time"
                value={newEvent.time}
                onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                className="bg-neutral-800 rounded px-3 py-2 text-base text-neutral-100"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-neutral-400 col-span-2">
              Lokation (valgfri)
              <input
                value={newEvent.location}
                onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                className="bg-neutral-800 rounded px-3 py-2 text-base text-neutral-100"
              />
            </label>
            {duplicate && (
              <div className="col-span-2 text-sm bg-amber-950/40 border border-amber-700 rounded p-3">
                Der findes allerede et event med navnet "{duplicate.name}".{" "}
                <button
                  onClick={() => setTargetId(duplicate.id)}
                  className="underline text-amber-300"
                >
                  Opdatér det i stedet
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {error && (
        <div className="bg-red-950/50 border border-red-800 text-red-200 rounded-xl p-3 text-sm">
          {error}
        </div>
      )}
      {busy && <div className="text-sm text-neutral-400">Læser arket…</div>}

      {result && (
        <>
          {result.warnings.length > 0 && (
            <div className="bg-amber-950/40 border border-amber-700 rounded-xl p-3 text-sm space-y-1">
              <div className="font-semibold text-amber-300">
                {result.warnings.length} ting i arket bør tjekkes
              </div>
              {result.warnings.map((w, i) => (
                <div key={i} className="text-neutral-300">
                  {w.sheetRow ? `Række ${w.sheetRow}` : `Kamp ${w.fightNumber}`}: {w.message}
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between gap-4 bg-neutral-900 border border-neutral-800 rounded-xl p-3">
            <div className="text-sm">
              {result.actions.length} kampe i "{result.sheet}"
              {target ? ` → ${target.name}` : " → nyt event"}:{" "}
              <span className="text-emerald-400">{creates} nye</span>
              {targetId && (
                <>
                  , <span className="text-amber-400">{updates} opdateres</span>,{" "}
                  <span className="text-neutral-400">{unchanged} uændrede</span>
                </>
              )}
            </div>
            <button
              onClick={apply}
              disabled={!canApply}
              className="tap-btn px-4 py-2 bg-emerald-600 disabled:opacity-40 text-sm"
            >
              {nothingToDo ? "Intet at importere" : busy ? "Importerer…" : "Importér"}
            </button>
          </div>

          <div className="space-y-2">
            {result.actions.map((a, i) => (
              <FightPreview key={i} index={i} action={a} />
            ))}
          </div>

          {result.onlyInSystem.length > 0 && (
            <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-3 text-sm space-y-1">
              <div className="font-semibold">
                Findes i eventet, men ikke i arket — røres ikke
              </div>
              {result.onlyInSystem.map((o) => (
                <div key={o.fightId} className="text-neutral-400">
                  {o.label}
                  {o.hasStats && " (har registrerede stats)"}
                </div>
              ))}
              <div className="text-xs text-neutral-500">
                Er en kamp aflyst eller en fighter udskiftet, så slet den gamle kamp
                manuelt på event-siden.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const BADGE: Record<ImportAction["kind"], [string, string]> = {
  create: ["Ny", "bg-emerald-800 text-emerald-100"],
  update: ["Opdateres", "bg-amber-700 text-white"],
  unchanged: ["Uændret", "bg-neutral-800 text-neutral-400"],
};

function FightPreview({ index, action }: { index: number; action: ImportAction }) {
  const f = action.fight;
  const [label, badge] = BADGE[action.kind];
  const meta = [
    f.startTime && `kl. ${f.startTime}`,
    f.weightClass && (f.weightLimitKg ? `${f.weightClass} (${dk(f.weightLimitKg)} kg)` : f.weightClass),
    f.level && (f.level === "pro" ? "Pro" : "Amatør"),
    f.totalRounds && f.roundDurationSec && `${f.totalRounds}×${dk(f.roundDurationSec / 60)} min`,
    f.sponsor && `Sponsor: ${f.sponsor}`,
  ].filter(Boolean);

  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-3 space-y-2">
      <div className="flex items-center gap-3 text-sm">
        <span className="text-neutral-500 w-6">#{index + 1}</span>
        <span className="flex-1 text-neutral-300">{meta.join(" · ")}</span>
        <span className={`text-xs uppercase px-2 py-0.5 rounded ${badge}`}>{label}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FighterPreview corner="blue" f={f.blue} match={action.fighters.blue} />
        <FighterPreview corner="red" f={f.red} match={action.fighters.red} />
      </div>
      {action.kind === "update" && action.similarNames && (
        <div className="text-xs bg-amber-950/40 border border-amber-700 rounded px-2 py-1 text-amber-200">
          Genkendt ud fra lignende navne, ikke et præcist match — tjek at det er
          samme kamp før du importerer.
        </div>
      )}
      {action.kind !== "unchanged" && action.changes.length > 0 && (
        <div className="text-xs border-t border-neutral-800 pt-2 space-y-0.5">
          {action.changes.map((c, i) => (
            <div key={i} className="text-neutral-300">
              {c.label}: <span className="text-neutral-500">{c.from ?? "–"}</span> →{" "}
              <span className="text-amber-300">{c.to ?? "–"}</span>
            </div>
          ))}
          {action.kind === "update" && action.hasStats && (
            <div className="text-neutral-500">Kampen har registrerede stats — de bevares.</div>
          )}
        </div>
      )}
    </div>
  );
}

const MATCHED_BY = {
  fight: "samme kamp",
  tapology: "Tapology-link",
  name: "navn",
} as const;

function MatchBadge({ match }: { match: ImportFighterMatch }) {
  if (match.kind === "new") {
    return (
      <span className="ml-2 text-[10px] uppercase px-1.5 py-0.5 rounded bg-emerald-900 text-emerald-200">
        Ny fighter
      </span>
    );
  }
  return (
    <span
      className="ml-2 text-[10px] uppercase px-1.5 py-0.5 rounded bg-neutral-800 text-neutral-300"
      title={`Profil "${match.name}", fundet via ${MATCHED_BY[match.matchedBy]}`}
    >
      Kendt · {match.fightCount} {match.fightCount === 1 ? "kamp" : "kampe"}
    </span>
  );
}

function FighterPreview({
  corner,
  f,
  match,
}: {
  corner: "blue" | "red";
  f: ImportFighter;
  match: ImportFighterMatch;
}) {
  const details = [
    f.age && `${f.age} år`,
    f.heightCm && `${f.heightCm} cm`,
    f.reachCm && `rækkevidde ${f.reachCm} cm`,
    f.weighInKg && `${dk(f.weighInKg)} kg`,
    f.record && (f.recordAfter ? `${f.record} → ${f.recordAfter}` : f.record),
  ].filter(Boolean);
  return (
    <div
      className={`border-l-4 pl-3 ${corner === "red" ? "border-red-600" : "border-blue-600"}`}
    >
      <div className="font-semibold">
        {f.name}
        {f.tapologyUrl && (
          <a
            href={f.tapologyUrl}
            target="_blank"
            rel="noreferrer"
            className="ml-2 text-xs font-normal text-neutral-500 hover:text-neutral-300"
          >
            Tapology ↗
          </a>
        )}
        <MatchBadge match={match} />
      </div>
      <div className="text-xs text-neutral-400">
        {[f.club, f.country].filter(Boolean).join(" · ") || "—"}
      </div>
      <div className="text-xs text-neutral-400">{details.join(" · ") || "—"}</div>
      {f.notes && <div className="text-xs text-neutral-500 italic">{f.notes}</div>}
    </div>
  );
}
